import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { LazyTurnstile } from "@/components/site/LazyTurnstile";
import { sendContactInquiry } from "@/lib/inquiries.functions";
import { measureConversion } from "@/lib/cta-measurement";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

interface EsContactFormProps {
  /** Prefix for input ids so two forms can never collide on one page. */
  idPrefix?: string;
  heading?: string;
  className?: string;
  /** Optional initial message, e.g. a benefits-check request from a CTA link. */
  prefillMessage?: string;
}

/**
 * Shared Spanish contact / appointment-request form. Used by /es/contacto and
 * the Spanish local page so a change here updates every Spanish form.
 */
export function EsContactForm({
  idPrefix = "es-contact",
  heading = "Envíenos un mensaje o solicite una cita",
  className = "h-full space-y-2 rounded-2xl border border-border bg-card p-4 shadow-sm md:p-5",
  prefillMessage = "",
}: EsContactFormProps) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submitContact = useServerFn(sendContactInquiry);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Por favor complete la verificación de seguridad antes de enviar.");
      return;
    }

    setSending(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const payload = {
      name: String(formData.get("name") || "").trim(),
      phone: String(formData.get("phone") || "").trim() || undefined,
      email: String(formData.get("email") || "").trim(),
      message: String(formData.get("message") || "").trim(),
      turnstileToken,
    };

    try {
      await submitContact({ data: payload });
      measureConversion("contact_request");
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ocurrió un error. Intente de nuevo.");
    } finally {
      setSending(false);
    }
  }

  return (
    <form ref={formRef} className={className} onSubmit={handleSubmit}>
      {sent ? (
        <div className="py-8 text-center">
          <h2 className="font-serif text-2xl text-brand-deep">Gracias</h2>
          <p className="mt-2 text-muted-foreground">
            Recibimos su mensaje. El consultorio del Dr. Fitzer se comunicará con usted pronto.
          </p>
        </div>
      ) : (
        <>
          <h2 className="font-serif text-2xl text-brand-deep">{heading}</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            <label htmlFor={`${idPrefix}-name`} className="text-sm">
              <span className="mb-0.5 block font-medium">
                Nombre<span aria-hidden="true" className="text-brand">*</span>
              </span>
              <input
                id={`${idPrefix}-name`}
                required
                type="text"
                name="name"
                autoComplete="name"
                className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand"
              />
            </label>
            <label htmlFor={`${idPrefix}-phone`} className="text-sm">
              <span className="mb-0.5 block font-medium">Teléfono</span>
              <input
                id={`${idPrefix}-phone`}
                type="tel"
                name="phone"
                autoComplete="tel"
                className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand"
              />
            </label>
          </div>
          <label htmlFor={`${idPrefix}-email`} className="block text-sm">
            <span className="mb-0.5 block font-medium">
              Correo electrónico<span aria-hidden="true" className="text-brand">*</span>
            </span>
            <input
              id={`${idPrefix}-email`}
              required
              type="email"
              name="email"
              autoComplete="email"
              className="w-full rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand"
            />
          </label>
          <label htmlFor={`${idPrefix}-message`} className="block text-sm">
            <span className="mb-0.5 block font-medium">
              Mensaje<span aria-hidden="true" className="text-brand">*</span>
            </span>
            <textarea
              id={`${idPrefix}-message`}
              required
              name="message"
              rows={3}
              defaultValue={prefillMessage}
              className="w-full resize-y rounded-lg border border-border bg-background px-3 py-1 outline-none focus:border-brand"
            />
          </label>
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-center">
            <LazyTurnstile
              siteKey={siteKey}
              triggerRef={formRef}
              onSuccess={(token) => setTurnstileToken(token)}
              onError={() => setError("La verificación de seguridad falló. Intente de nuevo.")}
              onExpire={() => setTurnstileToken(null)}
            />
          </div>
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={sending || !turnstileToken}
              className="rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {sending ? "Enviando..." : "Enviar mensaje"}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
